import React from "react";
import reject from "ramda/src/reject";
import propEq from "ramda/src/propEq";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import mergeLeft from "ramda/src/mergeLeft";
import Typography from "@mui/material/Typography";

import { Modal } from "../Modal";
import { Button } from "../../UI/Button";
import Api from "../../services/Api";
import { useNetwork } from "../../hooks/useNetwork";
import { EMAIL_ADDRESS, PHONE_NUMBER } from "../../constants/general";
import { Link } from "../Link";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import useAuth from "../../routes/login/AuthContext";
import { useModalContext } from "../../contexts/ModalContext/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Checkbox } from "../../UI/Checkbox";
import { propL1Id } from "../../helpers/propL1Id";
import { Network } from "../../services/NetworkApi";

interface IProps {
  l2Id: number;
}

const getTargets = (l1Id: number) => (selectedIds: Record<string, boolean>) =>
  Object.keys(selectedIds)
    .filter((id) => selectedIds[id])
    .map((l2Id) => ({
      l2Id: Number(l2Id),
      l1Id,
    }));

export const CopyGenderOptionsToOtherSubnetworksModal: React.FC<IProps> = (
  props
) => {
  const [selectedSubnetworksIds, setSelectedSubnetworksIds] = React.useState<
    Record<string, boolean>
  >({});
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const { getL2Options, getL2 } = useNetwork();
  const { setIsOpen } = useModalContext();
  const [isLoading, setIsLoading] = React.useState(false);

  const currentSubnetwork = getL2(selectedNetworkId, props.l2Id);
  const subnetworks = getL2Options(selectedNetworkId) as Network[];

  const getSubnetworks = reject(propEq("l2Id", props.l2Id));

  const toggleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSubnetworksIds(
      mergeLeft({ [event.target.name]: event.target.checked })
    );
  };

  const targets = React.useMemo(
    () => getTargets(selectedNetworkId)(selectedSubnetworksIds),
    [selectedSubnetworksIds, selectedNetworkId]
  );

  const handleClose = () => setIsOpen(false);

  const handleUpdate = async () => {
    setIsLoading(true);

    const res = await Api.copyGender(
      selectedNetworkId,
      {
        l1Id: selectedNetworkId,
        l2Id: props.l2Id,
        targets,
      },
      await getAuthToken()
    );

    if (res.success) {
      setIsOpen(false);
      showSuccessSnackbar("Successfully copied");
      loadNetworkInfoForNetworks(targets.map(propL1Id));
    } else {
      showErrorSnackbar("An error occurred. Please try again later!");
    }

    setIsLoading(false);
  };

  return (
    <Modal
      maxWidth="sm"
      title="Apply Gender Options to Other Matching Rounds"
      actions={
        <Grid container spacing={1} justifyContent="flex-end">
          <Grid item>
            <Button onClick={handleClose}>Cancel</Button>
          </Grid>
          <Grid item>
            <Button
              appearance="primary"
              isLoading={isLoading}
              onClick={handleUpdate}
            >
              Update Gender Options
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Box px={3} py={2}>
        <Typography gutterBottom>
          The current gender options for{" "}
          <strong>{currentSubnetwork?.l2Name}</strong> will replace the gender
          options for the matching rounds selected below. Please select the
          matching rounds to overwrite for gender options:
        </Typography>
        <Grid container>
          {getSubnetworks(subnetworks).map((subnetwork) => (
            <Grid item xs={12} key={subnetwork.l2Id}>
              <Checkbox
                onChange={toggleSelect}
                label={subnetwork.l2Name}
                name={String(subnetwork.l2Id)}
                isChecked={!!selectedSubnetworksIds[subnetwork.l2Id]}
              />
            </Grid>
          ))}
        </Grid>
        <Typography gutterBottom>
          If you have any questions, please contact us at{" "}
          <Link href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</Link> or call{" "}
          {PHONE_NUMBER}.
        </Typography>
      </Box>
    </Modal>
  );
};
